import { useEffect, useState, type ChangeEvent, type ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getAdminSettings, saveAdminSettings } from "@/services/adminService";
import type { AdminSystemSettings } from "@/types/admin";
import { uploadBrandingAsset } from "@/services/imageUploadService";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import {
  adminCard,
  adminInput,
  adminLabel,
  adminBtnPrimary,
  adminBtnGhost,
} from "@/pages/portal/admin/config/ui";

const EMPTY: AdminSystemSettings = {
  siteName: "", tagline: "", supportEmail: "", supportPhone: "", currency: "INR", timezone: "Asia/Kolkata",
  gstNumber: "", address: "", maintenanceMode: false, allowRegistrations: true, autoApproveListings: false,
  freePhotoLimit: 3, businessPhotoLimit: 8, premiumPhotoLimit: 10, premiumVideoLimit: 1,
  facebook: "", instagram: "", twitter: "", youtube: "",
  faviconUrl: "", fontUrl: "", fontFamily: "",
};

export default function SettingsSection() {
  const { accessToken } = useAuth();
  const { data, loading, error, reload } = useAdminResource(
    () => getAdminSettings(accessToken),
    EMPTY,
    [accessToken],
  );

  const [form, setForm]       = useState<AdminSystemSettings>(EMPTY);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [uploadingAsset, setUploadingAsset] = useState<null | "favicon" | "font">(null);
  const [assetError, setAssetError] = useState("");

  useEffect(() => {
    setForm(data);
  }, [data]);

  function set<K extends keyof AdminSystemSettings>(key: K, value: AdminSystemSettings[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  async function save() {
    setSaving(true);
    try {
      await saveAdminSettings(accessToken, form);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  }

  async function handleAssetUpload(event: ChangeEvent<HTMLInputElement>, kind: "favicon" | "font") {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setAssetError("");
    setUploadingAsset(kind);
    try {
      const url = await uploadBrandingAsset(file, kind, { token: accessToken });
      if (kind === "favicon") set("faviconUrl", url);
      else set("fontUrl", url);
    } catch (err) {
      setAssetError(err instanceof Error ? err.message : "Upload failed. Please try again.");
    } finally {
      setUploadingAsset(null);
    }
  }

  function removeFont() {
    set("fontUrl", "");
    set("fontFamily", "");
  }

  return (
    <div className="max-w-3xl space-y-6">
      {error && <AdminDataError message={error} onRetry={reload} />}

      {loading ? (
        <div className={`${adminCard} flex items-center justify-center py-16`}>
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
        </div>
      ) : (
        <>
          {/* Branding */}
          <Section title="Branding" icon="ri-palette-line">
            <Field label="Site name"><input className={adminInput} value={form.siteName} onChange={(e) => set("siteName", e.target.value)} /></Field>
            <Field label="Tagline"><input className={adminInput} value={form.tagline} onChange={(e) => set("tagline", e.target.value)} /></Field>
          </Section>

          {/* Branding assets — favicon + custom font */}
          <Section title="Branding assets" icon="ri-image-add-line">
            {form.fontUrl && (
              <style>{`@font-face{font-family:"${(form.fontFamily || "AdminBrandPreview").replace(/["\\]/g, "")}";src:url("${form.fontUrl}");font-display:swap;}`}</style>
            )}

            {/* Favicon */}
            <div className="flex flex-wrap items-center gap-4 rounded-lg border border-background-200 px-4 py-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-background-200 bg-background-100">
                {form.faviconUrl ? (
                  <img src={form.faviconUrl} alt="Favicon preview" className="h-8 w-8 object-contain" />
                ) : (
                  <i className="ri-image-2-line text-xl text-foreground-400" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground-900">Site favicon</p>
                <p className="text-xs text-foreground-500">The small icon in browser tabs. A square PNG, JPG or WebP works best.</p>
              </div>
              <div className="flex items-center gap-2">
                <label className={`${adminBtnGhost} ${uploadingAsset !== null ? "opacity-60" : ""}`}>
                  {uploadingAsset === "favicon" ? "Uploading…" : form.faviconUrl ? "Replace" : "Upload"}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => handleAssetUpload(e, "favicon")}
                    disabled={uploadingAsset !== null}
                  />
                </label>
                {form.faviconUrl && (
                  <button
                    type="button"
                    onClick={() => set("faviconUrl", "")}
                    className="cursor-pointer whitespace-nowrap text-xs font-semibold text-rose-600 hover:underline"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            {/* Custom font */}
            <div className="rounded-lg border border-background-200 px-4 py-3">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-background-100 text-foreground-500">
                  <i className="ri-font-size-2 text-xl" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground-900">Custom site font</p>
                  <p className="text-xs text-foreground-500">Upload a WOFF2, WOFF, TTF or OTF file to apply a custom font across the whole site.</p>
                </div>
                <div className="flex items-center gap-2">
                  <label className={`${adminBtnGhost} ${uploadingAsset !== null ? "opacity-60" : ""}`}>
                    {uploadingAsset === "font" ? "Uploading…" : form.fontUrl ? "Replace" : "Upload"}
                    <input
                      type="file"
                      accept=".woff2,.woff,.ttf,.otf"
                      className="hidden"
                      onChange={(e) => handleAssetUpload(e, "font")}
                      disabled={uploadingAsset !== null}
                    />
                  </label>
                  {form.fontUrl && (
                    <button
                      type="button"
                      onClick={removeFont}
                      className="cursor-pointer whitespace-nowrap text-xs font-semibold text-rose-600 hover:underline"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>

              {form.fontUrl && (
                <div className="mt-3 space-y-3 border-t border-background-100 pt-3">
                  <div>
                    <label className={adminLabel}>Font family name</label>
                    <input
                      className={adminInput}
                      value={form.fontFamily}
                      onChange={(e) => set("fontFamily", e.target.value)}
                      placeholder="e.g. Coastal Sans"
                    />
                  </div>
                  <div className="rounded-lg bg-background-100 px-4 py-3">
                    <p className="text-[11px] uppercase tracking-wider text-foreground-500">Preview</p>
                    <p
                      className="mt-1 text-lg font-semibold text-foreground-900"
                      style={{ fontFamily: form.fontFamily || "AdminBrandPreview" }}
                    >
                      The quick brown fox jumps over the lazy dog
                    </p>
                  </div>
                </div>
              )}
            </div>

            {assetError && <p className="text-xs font-semibold text-rose-600">{assetError}</p>}
          </Section>

          {/* Contact */}
          <Section title="Contact details" icon="ri-customer-service-2-line">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Support email"><input type="email" className={adminInput} value={form.supportEmail} onChange={(e) => set("supportEmail", e.target.value)} /></Field>
              <Field label="Support phone"><input type="tel" className={adminInput} value={form.supportPhone} onChange={(e) => set("supportPhone", e.target.value)} /></Field>
            </div>
            <Field label="GST number"><input className={adminInput} value={form.gstNumber} onChange={(e) => set("gstNumber", e.target.value)} /></Field>
            <Field label="Registered address"><textarea rows={2} className={`${adminInput} h-auto py-2.5`} value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
          </Section>

          {/* Localization */}
          <Section title="Localization" icon="ri-global-line">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Currency">
                <select className={`${adminInput} cursor-pointer`} value={form.currency} onChange={(e) => set("currency", e.target.value)}>
                  <option value="INR">INR — Indian Rupee</option>
                  <option value="USD">USD — US Dollar</option>
                </select>
              </Field>
              <Field label="Timezone">
                <select className={`${adminInput} cursor-pointer`} value={form.timezone} onChange={(e) => set("timezone", e.target.value)}>
                  <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                  <option value="UTC">UTC</option>
                </select>
              </Field>
            </div>
          </Section>

          {/* Plan limits */}
          <Section title="Plan limits" icon="ri-equalizer-line">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Field label="Free photos"><input type="number" min={0} className={adminInput} value={form.freePhotoLimit} onChange={(e) => set("freePhotoLimit", Number(e.target.value) || 0)} /></Field>
              <Field label="Business photos"><input type="number" min={0} className={adminInput} value={form.businessPhotoLimit} onChange={(e) => set("businessPhotoLimit", Number(e.target.value) || 0)} /></Field>
              <Field label="Premium photos"><input type="number" min={0} className={adminInput} value={form.premiumPhotoLimit} onChange={(e) => set("premiumPhotoLimit", Number(e.target.value) || 0)} /></Field>
              <Field label="Premium videos"><input type="number" min={0} className={adminInput} value={form.premiumVideoLimit} onChange={(e) => set("premiumVideoLimit", Number(e.target.value) || 0)} /></Field>
            </div>
          </Section>

          {/* Platform toggles */}
          <Section title="Platform behaviour" icon="ri-toggle-line">
            <Toggle label="Allow new registrations" desc="Let business owners and visitors create accounts." checked={form.allowRegistrations} onChange={(v) => set("allowRegistrations", v)} />
            <Toggle label="Auto-approve new listings" desc="Publish submitted listings immediately without manual review." checked={form.autoApproveListings} onChange={(v) => set("autoApproveListings", v)} />
            <Toggle label="Maintenance mode" desc="Show a maintenance page to visitors while keeping the admin panel available." checked={form.maintenanceMode} onChange={(v) => set("maintenanceMode", v)} danger />
          </Section>

          {/* Socials */}
          <Section title="Social profiles" icon="ri-share-circle-line">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Facebook"><input className={adminInput} value={form.facebook} onChange={(e) => set("facebook", e.target.value)} placeholder="https://" /></Field>
              <Field label="Instagram"><input className={adminInput} value={form.instagram} onChange={(e) => set("instagram", e.target.value)} placeholder="https://" /></Field>
              <Field label="X / Twitter"><input className={adminInput} value={form.twitter} onChange={(e) => set("twitter", e.target.value)} placeholder="https://" /></Field>
              <Field label="YouTube"><input className={adminInput} value={form.youtube} onChange={(e) => set("youtube", e.target.value)} placeholder="https://" /></Field>
            </div>
          </Section>

          <div className="sticky bottom-4 flex items-center justify-end gap-3 rounded-xl border border-background-200 bg-background-50 px-5 py-3">
            {saved && (
              <span className="text-xs font-semibold text-emerald-600">
                <i className="ri-checkbox-circle-line mr-1" /> Settings saved
              </span>
            )}
            <button type="button" disabled={saving} onClick={save} className={adminBtnPrimary}>
              {saving
                ? <><i className="ri-loader-4-line animate-spin text-sm" /> Saving…</>
                : <><i className="ri-save-line text-sm" /> Save settings</>}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon: string; children: ReactNode }) {
  return (
    <div className={`${adminCard} p-6`}>
      <h3 className="mb-5 flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
        <span className="flex h-5 w-5 items-center justify-center text-primary-600"><i className={`${icon} text-base`} /></span>
        {title}
      </h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className={adminLabel}>{label}</label>
      {children}
    </div>
  );
}

function Toggle({ label, desc, checked, onChange, danger = false }: { label: string; desc: string; checked: boolean; onChange: (v: boolean) => void; danger?: boolean }) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4 rounded-lg border border-background-200 px-4 py-3">
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-foreground-900">{label}</span>
        <span className="block text-xs text-foreground-500">{desc}</span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors ${
          checked ? (danger ? "bg-rose-500" : "bg-primary-500") : "bg-background-300"
        }`}
      >
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
      </button>
    </label>
  );
}